import { useContext, useState } from "react";
import styled from "styled-components";
import { FilterContext, FilterDispatchContext } from "../../Context/FilterContext";

const Panel = styled.div`
  display: none;
  background: #fff;
  border-radius: 0 0 12px 12px;
  box-shadow: var(--elevation-1dp);

  &.open {
    display: block;
    margin-top: 12px;
    padding: 24px 0;
  }

  @media screen and (max-width: 767px) {
    &.open {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      max-height: 70vh;
      overflow-y: auto;
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
      padding: 24px 16px;
    }
  }

  .panel-inner {
    max-width: var(--container-max-width);
    margin: 0 16px;
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;

    @media screen and (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      margin: 0 16px;
    }

    @media screen and (min-width: 1440px) {
      grid-template-columns: repeat(3, 1fr);
      margin: 0 auto;
      padding: 0 56px;
    }
  }

  .filter-group {
    h4 {
      font-size: 13px;
      font-weight: 600;
      color: var(--primary-grey-300);
      margin-bottom: 8px;
      font-family: var(--font-secondary);
    }
  }

  .btn-group {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;

    button {
      padding: 8px 14px;
      border: 1px solid var(--primary-grey-200);
      border-radius: 8px;
      background: #fff;
      font-size: 13px;
      color: var(--primary-grey-500);
      cursor: pointer;
      transition: all 150ms;

      &:hover {
        border-color: var(--color-secondary-300);
      }

      &.active {
        background: var(--primary-100);
        border-color: var(--color-primary);
        color: var(--color-secondary-600);
        font-weight: 600;
      }
    }
  }

  .area-inputs {
    display: flex;
    gap: 8px;
    align-items: center;

    input {
      width: 100px;
      padding: 8px 10px;
      border: 1px solid var(--primary-grey-200);
      border-radius: 6px;
      font-size: 13px;
      outline: none;

      &:focus {
        border-color: var(--color-primary);
      }

      &::placeholder {
        color: var(--color-secondary-200);
      }
    }

    span {
      color: var(--primary-grey-200);
    }
  }

  .furnishing-group {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;

    label {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: var(--primary-grey-500);
      cursor: pointer;

      input[type="checkbox"] {
        accent-color: var(--color-primary);
        width: 16px;
        height: 16px;
      }
    }
  }

  .panel-actions {
    grid-column: 1 / -1;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid var(--background-grey-200);

    button {
      padding: 10px 20px;
      border-radius: 100px;
      font-size: 14px;
      cursor: pointer;
      transition: all 150ms;
    }

    .panel-clear {
      background: transparent;
      border: none;
      color: var(--color-secondary);

      &:hover {
        text-decoration: underline;
      }
    }

    .panel-apply {
      background: linear-gradient(135deg, var(--color-primary), #C4922E);
      color: #fff;
      border: none;
      font-weight: 700;

      &:hover {
        opacity: 0.9;
      }
    }
  }
`;

const Overlay = styled.div`
  display: none;

  @media screen and (max-width: 767px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.4);
    z-index: 999;
  }
`;

const BEDROOM_OPTIONS = ["Any", "1+", "2+", "3+", "4+", "5+"];
const BATHROOM_OPTIONS = ["Any", "1+", "2+", "3+", "4+"];
const PARKING_OPTIONS = ["Any", "1+", "2+", "3+"];
const FURNISHING_OPTIONS = [
  { value: "fully_furnished", label: "Fully Furnished" },
  { value: "semi_furnished", label: "Semi Furnished" },
  { value: "bare", label: "Bare / Unfurnished" },
];

const parseCountValue = (label) => {
  if (label === "Any") return "";
  return parseInt(label, 10).toString();
};

const MoreFiltersPanel = ({ isOpen, onClose }) => {
  const filters = useContext(FilterContext) || {};
  const dispatch = useContext(FilterDispatchContext);

  const [local, setLocal] = useState({
    bedrooms: filters.bedrooms || "",
    bathrooms: filters.bathrooms || "",
    parking: filters.parking || "",
    floorAreaMin: filters.floorAreaMin || "",
    floorAreaMax: filters.floorAreaMax || "",
    lotAreaMin: filters.lotAreaMin || "",
    lotAreaMax: filters.lotAreaMax || "",
    furnishing: filters.furnishing || "",
  });

  const setField = (key, value) => {
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const getActiveLabel = (value, options) => {
    if (!value) return "Any";
    return options.find((o) => parseCountValue(o) === value) || "Any";
  };

  const handleApply = () => {
    const keys = ["bedrooms", "bathrooms", "parking", "floorAreaMin", "floorAreaMax", "lotAreaMin", "lotAreaMax", "furnishing"];
    keys.forEach((key) => {
      if (local[key]) {
        dispatch({ action: "set", filter: { key, value: local[key] } });
      } else {
        dispatch({ action: "delete", filter: { key } });
      }
    });
    onClose();
  };

  const handleClear = () => {
    const cleared = {
      bedrooms: "", bathrooms: "", parking: "",
      floorAreaMin: "", floorAreaMax: "",
      lotAreaMin: "", lotAreaMax: "",
      furnishing: "",
    };
    setLocal(cleared);
    Object.keys(cleared).forEach((key) => {
      dispatch({ action: "delete", filter: { key } });
    });
    onClose();
  };

  const handleFurnishingToggle = (value) => {
    const current = local.furnishing ? local.furnishing.split(",") : [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setField("furnishing", updated.join(","));
  };

  return (
    <>
      {isOpen && <Overlay onClick={onClose} />}
      <Panel className={isOpen ? "open" : ""}>
        <div className="panel-inner">
          <div className="filter-group">
            <h4>Bedrooms</h4>
            <div className="btn-group">
              {BEDROOM_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={getActiveLabel(local.bedrooms, BEDROOM_OPTIONS) === opt ? "active" : ""}
                  onClick={() => setField("bedrooms", parseCountValue(opt))}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h4>Bathrooms</h4>
            <div className="btn-group">
              {BATHROOM_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={getActiveLabel(local.bathrooms, BATHROOM_OPTIONS) === opt ? "active" : ""}
                  onClick={() => setField("bathrooms", parseCountValue(opt))}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h4>Parking</h4>
            <div className="btn-group">
              {PARKING_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={getActiveLabel(local.parking, PARKING_OPTIONS) === opt ? "active" : ""}
                  onClick={() => setField("parking", parseCountValue(opt))}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h4>Floor Area (sqm)</h4>
            <div className="area-inputs">
              <input
                type="text"
                placeholder="Min"
                value={local.floorAreaMin}
                onChange={(e) => setField("floorAreaMin", e.target.value.replace(/\D/g, ""))}
              />
              <span>&mdash;</span>
              <input
                type="text"
                placeholder="Max"
                value={local.floorAreaMax}
                onChange={(e) => setField("floorAreaMax", e.target.value.replace(/\D/g, ""))}
              />
            </div>
          </div>

          <div className="filter-group">
            <h4>Lot Area (sqm)</h4>
            <div className="area-inputs">
              <input
                type="text"
                placeholder="Min"
                value={local.lotAreaMin}
                onChange={(e) => setField("lotAreaMin", e.target.value.replace(/\D/g, ""))}
              />
              <span>&mdash;</span>
              <input
                type="text"
                placeholder="Max"
                value={local.lotAreaMax}
                onChange={(e) => setField("lotAreaMax", e.target.value.replace(/\D/g, ""))}
              />
            </div>
          </div>

          <div className="filter-group">
            <h4>Furnishing</h4>
            <div className="furnishing-group">
              {FURNISHING_OPTIONS.map((opt) => (
                <label key={opt.value}>
                  <input
                    type="checkbox"
                    checked={(local.furnishing || "").split(",").includes(opt.value)}
                    onChange={() => handleFurnishingToggle(opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="panel-actions">
            <button type="button" className="panel-clear" onClick={handleClear}>Clear</button>
            <button type="button" className="panel-apply" onClick={handleApply}>Apply Filters</button>
          </div>
        </div>
      </Panel>
    </>
  );
};

export default MoreFiltersPanel;
